import React from "react";
import { useTranslate } from "@refinedev/core";
import { Modal, Card, Descriptions, Space, Tag, Button, Badge, Tooltip, message, theme } from "antd";
import { GlassCard } from "../../../../shared/components";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";

export interface OrderFileDetailsModalProps {
    open: boolean;
    onCancel: () => void;
    fileData: any;
    onDownload: () => void;
}

const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
        case "analyzed":
            return "green";
        case "pending":
            return "orange";
        case "error":
            return "red";
        default:
            return "default";
    }
};

const getStatusText = (status: string) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
};

export const OrderFileDetailsModal: React.FC<OrderFileDetailsModalProps> = ({
    open,
    onCancel,
    fileData,
    onDownload,
}) => {
    const _t = useTranslate();
    const { token } = theme.useToken();

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            message.success(_t("orders.automationTasks.fileDetailsModal.copied"));
        }).catch(() => {
            message.error(_t("orders.automationTasks.fileDetailsModal.copyFailed"));
        });
    };

    if (!fileData) return null;

    return (
        <Modal
            title={_t("orders.automationTasks.fileDetailsModal.title")}
            open={open}
            onCancel={onCancel}
            width={1000}
            footer={null}
        >
            <div className="max-h-[80vh] overflow-y-auto">
                <GlassCard title={_t("orders.automationTasks.fileDetailsModal.basicInfo")} className="mb-4">
                    <Descriptions bordered column={1} size="small">
                        <Descriptions.Item label="ID">{fileData._id}</Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.projectRef")}>
                            <Space>
                                {fileData.projectRef}
                                <Tooltip title={_t("orders.automationTasks.fileDetailsModal.copyProjectRef")}>
                                    <Button
                                        type="text"
                                        icon={<CopyOutlined />}
                                        size="small"
                                        onClick={() => copyToClipboard(fileData.projectRef)}
                                    />
                                </Tooltip>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.type")}>{fileData.type}</Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.version")}>{fileData.version}</Descriptions.Item>
                        {fileData.tags && fileData.tags.length > 0 && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.tags")}>
                                <Space size={[0, 8]} wrap>
                                    {fileData.tags.map((tag: string, index: number) => (
                                        <Tag key={index} color="green">
                                            {tag}
                                        </Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.status")}>
                            <Tag color={getStatusColor(fileData.status)}>
                                {getStatusText(fileData.status)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.createdAt")}>
                            {new Date(fileData.createdAt).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.updatedAt")}>
                            {new Date(fileData.updatedAt).toLocaleString()}
                        </Descriptions.Item>
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.binaryAvailable")}>
                            {fileData.binaryPath ? (
                                <Tag color="green">{_t("orders.automationTasks.fileDetailsModal.yes")}</Tag>
                            ) : (
                                <Tag color="red">{_t("orders.automationTasks.fileDetailsModal.no")}</Tag>
                            )}
                        </Descriptions.Item>
                        {fileData.downloadUrl && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.download")}>
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={onDownload}
                                >
                                    {_t("orders.automationTasks.fileDetailsModal.downloadFile")}
                                </Button>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </GlassCard>

                <GlassCard title={_t("orders.automationTasks.fileDetailsModal.vehicleInfo")} className="mb-4">
                    <Descriptions bordered column={2} size="small">
                        {fileData.vehicleType && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.vehicleType")}>{fileData.vehicleType}</Descriptions.Item>
                        )}
                        {fileData.vehicleProducer && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.vehicleProducer")}>{fileData.vehicleProducer}</Descriptions.Item>
                        )}
                        {fileData.vehicleSeries && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.vehicleSeries")}>{fileData.vehicleSeries}</Descriptions.Item>
                        )}
                        {fileData.vehicleBuild && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.vehicleBuild")}>{fileData.vehicleBuild}</Descriptions.Item>
                        )}
                        {fileData.vehicleModel && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.vehicleModel")}>{fileData.vehicleModel}</Descriptions.Item>
                        )}
                        {fileData.vehicleModelyear && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.modelYear")}>{fileData.vehicleModelyear}</Descriptions.Item>
                        )}
                        {fileData.userdef1 && (
                            <Descriptions.Item label="User Def 1">{fileData.userdef1}</Descriptions.Item>
                        )}
                        {fileData.userdef2 && (
                            <Descriptions.Item label="User Def 2">{fileData.userdef2}</Descriptions.Item>
                        )}
                        {fileData.userdef4 && (
                            <Descriptions.Item label="User Def 4">{fileData.userdef4}</Descriptions.Item>
                        )}
                    </Descriptions>
                </GlassCard>

                <GlassCard title={_t("orders.automationTasks.fileDetailsModal.ecuInfo")} className="mb-4">
                    <Descriptions bordered column={2} size="small">
                        {fileData.ecuUse && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.ecuUse")}>{fileData.ecuUse}</Descriptions.Item>
                        )}
                        {fileData.ecuProducer && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.ecuProducer")}>{fileData.ecuProducer}</Descriptions.Item>
                        )}
                        {fileData.ecuBuild && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.ecuBuild")}>{fileData.ecuBuild}</Descriptions.Item>
                        )}
                        {fileData.ecuProdNr && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.ecuProdNr")}>{fileData.ecuProdNr}</Descriptions.Item>
                        )}
                        {fileData.ecuStgNr && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.ecuStgNr")}>{fileData.ecuStgNr}</Descriptions.Item>
                        )}
                        {fileData.ecuSoftwareVersion && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.softwareVersion")}>{fileData.ecuSoftwareVersion}</Descriptions.Item>
                        )}
                        {fileData.engineType && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.engineType")}>{fileData.engineType}</Descriptions.Item>
                        )}
                        {fileData.engineDisplacement && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.engineDisplacement")}>{fileData.engineDisplacement}</Descriptions.Item>
                        )}
                        {fileData.engineTransmission && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.transmission")}>{fileData.engineTransmission}</Descriptions.Item>
                        )}
                        {fileData.outputPS && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.outputPS")}>{fileData.outputPS}</Descriptions.Item>
                        )}
                        {fileData.outputKW && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.outputKW")}>{fileData.outputKW}</Descriptions.Item>
                        )}
                    </Descriptions>
                </GlassCard>

                <GlassCard title={_t("orders.automationTasks.fileDetailsModal.analysisInfo")} className="mb-4">
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.status")}>
                            <Tag color={getStatusColor(fileData.status)}>
                                {getStatusText(fileData.status)}
                            </Tag>
                        </Descriptions.Item>
                        {fileData.analyzedAt && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.analyzedAt")}>
                                {new Date(fileData.analyzedAt).toLocaleString()}
                            </Descriptions.Item>
                        )}
                        {fileData.error && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.error")} span={2}>
                                <div className="bg-red-50 border border-red-200 p-2 rounded text-red-600">
                                    {fileData.error}
                                </div>
                            </Descriptions.Item>
                        )}
                        {fileData.controlUnitFamily && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.controlUnitFamily")}>
                                <Tag color="purple">{fileData.controlUnitFamily}</Tag>
                            </Descriptions.Item>
                        )}
                        {fileData.isHiLo !== undefined && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.hiLoByteOrder")}>
                                <Tag color={fileData.isHiLo ? "green" : "orange"}>
                                    {fileData.isHiLo ? _t("orders.automationTasks.fileDetailsModal.highByteFirst") : _t("orders.automationTasks.fileDetailsModal.lowByteFirst")}
                                </Tag>
                            </Descriptions.Item>
                        )}
                        {fileData.blocks && fileData.blocks.length > 0 && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.totalBlocks")}>
                                <Badge count={fileData.blocks.length} style={{ backgroundColor: token.colorInfo }} />
                            </Descriptions.Item>
                        )}
                        {fileData.blocks && fileData.blocks.length > 0 && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.blockTypes")}>
                                <Space wrap>
                                    {Array.from(new Set(fileData.blocks.map((b: any) => b.type))).map((type: any) => (
                                        <Tag key={type} color="blue">
                                            Type {type}
                                        </Tag>
                                    ))}
                                </Space>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </GlassCard>

                <GlassCard title={_t("orders.automationTasks.fileDetailsModal.projectInfo")} className="mb-4">
                    <Descriptions bordered column={2} size="small">
                        {fileData.readingHardware && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.readingHardware")}>{fileData.readingHardware}</Descriptions.Item>
                        )}
                        {fileData.projectType && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.projectType")}>{fileData.projectType}</Descriptions.Item>
                        )}
                        {fileData.numVersions && (
                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.numVersions")}>{fileData.numVersions}</Descriptions.Item>
                        )}
                    </Descriptions>
                </GlassCard>

                <GlassCard>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-base font-semibold">{_t("orders.automationTasks.fileDetailsModal.blocksInfo")}</span>
                        {fileData.blocks && fileData.blocks.length > 0 && (
                            <Badge count={fileData.blocks.length} style={{ backgroundColor: token.colorInfo }} />
                        )}
                    </div>
                    {fileData.blocks && fileData.blocks.length > 0 ? (
                        <div className="max-h-[400px] overflow-y-auto">
                            {fileData.blocks.map((block: any, index: number) => (
                                <Card
                                    key={index}
                                    size="small"
                                    className="mb-2 border border-gray-200 rounded-md"
                                    title={
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold">Block {index + 1}</span>
                                            <Tag color="blue">Type {block.type}</Tag>
                                        </div>
                                    }
                                >
                                    <Descriptions size="small" column={2}>
                                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.blockId")}>
                                            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                                                {block.id}
                                            </code>
                                        </Descriptions.Item>
                                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.size")}>
                                            <Tag color="green">{block.length} bytes</Tag>
                                        </Descriptions.Item>
                                        {(block.physicalAddress !== undefined || block.endblockPhysicalAddress !== undefined) && (
                                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.physicalAddress")}>
                                                <Space>
                                                    {block.physicalAddress !== undefined && (
                                                        <Tag color="blue">0x{Number(block.physicalAddress).toString(16).toUpperCase()}</Tag>
                                                    )}
                                                    {block.physicalAddress !== undefined && block.endblockPhysicalAddress !== undefined && (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                    {block.endblockPhysicalAddress !== undefined && (
                                                        <Tag color="purple">0x{Number(block.endblockPhysicalAddress).toString(16).toUpperCase()}</Tag>
                                                    )}
                                                </Space>
                                            </Descriptions.Item>
                                        )}
                                        {(block.logicalAddress !== undefined || block.endblockLogicalAddress !== undefined) && (
                                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.logicalAddress")}>
                                                <Space>
                                                    {block.logicalAddress !== undefined && (
                                                        <Tag color="cyan">0x{Number(block.logicalAddress).toString(16).toUpperCase()}</Tag>
                                                    )}
                                                    {block.logicalAddress !== undefined && block.endblockLogicalAddress !== undefined && (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                    {block.endblockLogicalAddress !== undefined && (
                                                        <Tag color="magenta">0x{Number(block.endblockLogicalAddress).toString(16).toUpperCase()}</Tag>
                                                    )}
                                                </Space>
                                            </Descriptions.Item>
                                        )}
                                        {block.physicalToLogicalOffset !== undefined && (
                                            <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.physicalToLogicalOffset")}>
                                                <Tag color="orange">{block.physicalToLogicalOffset}</Tag>
                                            </Descriptions.Item>
                                        )}
                                        <Descriptions.Item label={_t("orders.automationTasks.fileDetailsModal.hash")} span={2}>
                                            <div className="bg-gray-100 p-1 rounded text-[10px] font-mono break-all max-w-full">
                                                {block.hash}
                                            </div>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5 text-gray-400 italic">
                            {_t("orders.automationTasks.fileDetailsModal.noBlocksInfo")}
                        </div>
                    )}
                </GlassCard>
            </div>
        </Modal>
    );
};
